"use client";

import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";

import {
  fetchTwitchStreamByUser,
  fetchTwitchStreams,
  fetchTwitchUserInfo,
} from "../model/twitch";

import { TwitchStream } from "./twitch-api";

export interface StreamData {
  id: string;
  title: string;
  streamer: string;
  streamerAvatar: string;
  thumbnail: string;
  url: string;
  viewers: number;
  duration: string;
  startTime: string;
  platform: "twitch" | "youtube" | "vimeo";
  isLive: boolean;
}

// Hook for fetching live streams
export function useTwitchStreams(gameId?: string, limit: number = 20) {
  const tUi = useTranslations("Ui");
  const query = useQuery({
    queryKey: ["twitch", "streams", gameId, limit],
    queryFn: async () => {
      const result = await fetchTwitchStreams({ gameId: gameId ?? "", limit });
      if (result.success && result.streams) {
        return result.streams;
      } else {
        throw new Error(result.error || tUi("litUseTwitch.oshibkaPolucheniyaStrimov"));
      }
    },
    staleTime: 30 * 1000, // 30 секунд для стримов
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    streams: query.data ?? [],
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// Hook for fetching a specific stream
export interface UseTwitchStreamOptions {
  /** Периодический повторный опрос (мс). По умолчанию — только при remount/invalidate. */
  refetchIntervalMs?: number;
  refetchOnWindowFocus?: boolean;
}

export function useTwitchStream(
  userLogin: string,
  options?: UseTwitchStreamOptions,
) {
  const tUi = useTranslations("Ui");
  const query = useQuery({
    queryKey: ["twitch", "stream", userLogin],
    queryFn: async () => {
      if (!userLogin) return null;

      const result = await fetchTwitchStreamByUser({ userLogin });

      if (result.status === "ok" && result.stream) {
        // Конвертируем StreamData обратно в TwitchStream для совместимости
        const twitchStream: TwitchStream = {
          id: result.stream.id,
          user_id: result.stream.streamer,
          user_login: result.stream.url.split("/").pop() || "",
          user_name: result.stream.streamer,
          game_id: "",
          game_name: "",
          type: result.stream.isLive ? "live" : "vod",
          title: result.stream.title,
          viewer_count: result.stream.viewers,
          started_at: result.stream.startTime,
          language: "ru",
          thumbnail_url: result.stream.thumbnail,
          tag_ids: [],
          is_mature: false,
        };
        return twitchStream;
      }
      if (result.status === "error") {
        throw new Error(result.error || tUi("litUseTwitch.oshibkaTwitchApi"));
      }
      return null;
    },
    enabled: !!userLogin,
    staleTime: 30 * 1000, // 30 секунд
    retry: false,
    refetchOnWindowFocus: options?.refetchOnWindowFocus ?? false,
    refetchOnReconnect: false,
    ...(options?.refetchIntervalMs
      ? { refetchInterval: options.refetchIntervalMs }
      : {}),
  });

  return {
    stream: query.data ?? null,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// Hook for fetching user information
export function useTwitchUser(userLogin: string) {
  const tUi = useTranslations("Ui");
  const query = useQuery({
    queryKey: ["twitch", "user", userLogin],
    queryFn: async () => {
      if (!userLogin) return null;

      const result = await fetchTwitchUserInfo({ userLogin });

      if (result.status === "ok") return result.user ?? null;
      if (result.status === "error") {
        throw new Error(result.error || tUi("litUseTwitch.oshibkaTwitchApi"));
      }
      return null;
    },
    enabled: !!userLogin,
    staleTime: 5 * 60 * 1000, // 5 минут
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    user: query.data ?? null,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}
